import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Entry } from '@ionic-native/file/ngx';

export enum DocumentViewMode {
  FULL,
  FOLDER_SELECTOR,
  DOCUMENT_SELECTOR
}

export enum DocumentType {
  FOLDER,
  IMAGE,
  TEXT,
  PDF,
  OTHER
}

export enum PolicyDocumentPrefix {
  POLICY = 'Póliza_',
  GENERAL_CONDITIONS = 'Condiciones_Generales_',
  RENEWAL_NOTICE = 'Aviso_Renovación_',
  LIVE_CERTIFICATE = 'Certificado_',
  ACCIDENT_CERTIFICATE = 'Certificado_Accidentes_'
}

export interface Document {
  name: string;
  fullName: string;
  extension: string;
  fullPath: string;
  urlPreview: SafeResourceUrl;
  type: DocumentType;
  iconType: string;
  modificationTime: Date;
  size: number;
  entry: Entry;
  data: string | Blob | ArrayBuffer;
}

export interface DocumentTag {
  documentName: string;
  documentPath: string;
  tags: string[];
}

export enum DocumentErrorKey {
  DOCUMENTAL_MANAGEMENT_NOT_FOUND = 'DOCUMENTAL_MANAGEMENT.DOCUMENT.NOT_FOUND'
}

export interface DocumentError {
  headers: any;
  status: number;
  statusText: string;
  url: string;
  ok: boolean;
  name: string;
  message: string;
  error: {
    errorKey: DocumentErrorKey;
    errorDescription: string;
    errorType: string;
    requestId: string;
  };
}

export enum PolicyDocumentType {
  POLICY,
  GENERAL_CONDITIONS,
  RENEWAL_NOTICE,
  LIVE_BOUNDARY,
  LIVE_CERTIFICATE,
  INDIVIDUAL_CERTIFICATE,
  CIVIL_LIABILITY,
  ACCIDENT_CERTIFICATE
}

export function buildDocument(
  path: string,
  domSanitizer: DomSanitizer,
  entry?: Entry
) {
  const document = {
    name: null,
    fullName: null,
    extension: null,
    fullPath: null,
    urlPreview: null,
    type: DocumentType.FOLDER,
    iconType: null,
    modificationTime: null,
    size: null,
    entry: null,
    data: null
  } as Document;
  if (entry) {
    document.fullName = entry.name;
    updateTypeAndExtension(document, entry);
    if (isFile(document)) {
      document.name = document.fullName.substring(
        0,
        document.fullName.length - document.extension.length - 1
      );
    } else {
      document.name = document.fullName;
    }
    document.fullPath = `${path}/${document.fullName}`;
    const ionicUrl = (window as any).Ionic.WebView.convertFileSrc(
      document.fullPath
    );
    document.urlPreview = domSanitizer.bypassSecurityTrustResourceUrl(ionicUrl);
    document.entry = entry;
  }
  return document;
}

export function isFile(document: Document) {
  return document && document.type !== DocumentType.FOLDER;
}

export function isFolder(document: Document) {
  return document && document.type === DocumentType.FOLDER;
}

export function getPath(document: Document) {
  if (document.fullName && document.fullPath) {
    return document.fullPath.substring(
      0,
      document.fullPath.length - document.fullName.length - 1
    );
  }
  return null;
}

export function toArray(
  path: string,
  entries: Entry[],
  domSanitizer: DomSanitizer
) {
  const documents = [];
  if (entries != null) {
    for (const entry of entries) {
      documents.push(buildDocument(path, domSanitizer, entry));
    }
  }
  return documents;
}

export function orderByNameAndType(a: Document, b: Document) {
  if (isFolder(a) && isFile(b)) {
    return -1;
  }
  if (isFile(a) && isFolder(b)) {
    return 1;
  }
  const nameA = a.fullName.toUpperCase();
  const nameB = b.fullName.toUpperCase();
  if (nameA < nameB) {
    return -1;
  }
  if (nameA > nameB) {
    return 1;
  }
  return 0;
}

function updateTypeAndExtension(document: Document, entry: Entry) {
  document.extension = '';
  if (entry.isDirectory) {
    document.type = DocumentType.FOLDER;
  } else {
    const tokens = entry.name.split('.');
    if (tokens.length > 1) {
      document.extension = tokens.pop().toLowerCase();
    }
    switch (document.extension) {
      case 'jpeg':
      case 'jpg':
      case 'png':
      case 'gif':
        document.type = DocumentType.IMAGE;
        break;
      case 'pdf':
        document.type = DocumentType.PDF;
        break;
      case 'txt':
        document.type = DocumentType.TEXT;
        break;
      default:
        document.type = DocumentType.OTHER;
    }
  }
  updateIconType(document);
}

function updateIconType(document: Document) {
  switch (document.type) {
    case DocumentType.FOLDER:
      document.iconType = 'assets/images/documents/icon_folder.png';
      break;
    case DocumentType.IMAGE:
      document.iconType = 'assets/images/documents/icon_file_image.png';
      break;
    case DocumentType.PDF:
      document.iconType = 'assets/images/documents/icon_file_pdf.png';
      break;
    case DocumentType.TEXT:
      document.iconType = 'assets/images/documents/icon_file_text.png';
      break;
    default:
      document.iconType = 'assets/images/documents/icon_file.png';
      break;
  }
}

export enum DocumentTypes {
  RECEIPT = 'RECEIPT',
  DUPLICATE_FISCAL_CERTIFICATE = 'DUPLICATE_FISCAL_CERTIFICATE',
  QUATERLY = 'QUATERLY',
  INBOX_DOCUMENT = 'INBOX_DOCUMENT',
  COPAYMENT = 'COPAYMENT'
}
interface QuaterlyReportsDownloadRO {
  type: DocumentTypes.QUATERLY;
  payload: { exercise: string; period: string; codProduct: string };
}
interface ReceiptsDownloadRO {
  type: DocumentTypes.RECEIPT;
  payload: { receiptNumber: string };
}
interface FiscalCertificateDownloadRO {
  type: DocumentTypes.DUPLICATE_FISCAL_CERTIFICATE;
  payload: { exercise?: string; model?: string; codProduct?: string };
}
interface InboxDocumentsDownloadRO {
  type: DocumentTypes.INBOX_DOCUMENT;
  payload: { subject?: string; dateTime?: string };
}
interface CopaymentDownloadRO {
  type: DocumentTypes.COPAYMENT;
  payload: { copaymentDocumentNumber: string; };
}

export type DocumentROParams =
  ReceiptsDownloadRO | FiscalCertificateDownloadRO | QuaterlyReportsDownloadRO | CopaymentDownloadRO | InboxDocumentsDownloadRO;


export const HIDE_PARTICULAR_CONDITIONS_CIFS = ['G20877858'];
